import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { templateKeys } from './useTemplates';
import type { MessageState } from '../stores/bulkSendStore';
import type { SendTemplateMessageRequest } from '../../lib/types/template.types';

const RATE_LIMIT_MS = 2000; // 2 seconds between messages

interface BulkSendProgress {
  total: number;
  sent: number;
  failed: number;
  current: number;
}

interface BulkSendResult {
  total: number;
  sent: number;
  failed: number;
  errors: Array<{ clientId: string; clientName: string; error: string }>;
}

type ProgressCallback = (
  clientId: string,
  status: MessageState['status'],
  error?: string
) => void;

/**
 * Hook for bulk sending templates with rate limiting
 * Sends messages sequentially with 2-second delay between each
 *
 * @example
 * const { sendBulk, sending, progress } = useBulkSendTemplate();
 * await sendBulk(templateId, messages, (clientId, status, error) => {
 *   updateStatus(clientId, status, error);
 * });
 */
export function useBulkSendTemplate() {
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<BulkSendProgress>({
    total: 0,
    sent: 0,
    failed: 0,
    current: 0,
  });

  const sendBulk = useCallback(
    async (
      templateId: string,
      messages: MessageState[],
      onProgress: ProgressCallback
    ): Promise<BulkSendResult> => {
      setSending(true);
      const result: BulkSendResult = {
        total: messages.length,
        sent: 0,
        failed: 0,
        errors: [],
      };

      setProgress({
        total: messages.length,
        sent: 0,
        failed: 0,
        current: 0,
      });

      for (let i = 0; i < messages.length; i++) {
        const message = messages[i];

        // Update current message in progress
        setProgress((prev) => ({ ...prev, current: i + 1 }));

        // Update status to sending
        onProgress(message.clientId, 'sending');

        try {
          // Prepare request payload
          const payload: SendTemplateMessageRequest = {
            template_id: templateId,
            jid: message.jid,
            client_id: message.clientId,
            // If message was edited, we'll send the edited text
            // The backend will still use client_id for auto-fill
            // but the edited text will override the rendered result
            variables: message.editedText ? { _override_text: message.editedText } : undefined,
          };

          // Send template
          await api.post('/api/whatsapp/send-from-template', payload);

          // Success - update progress
          result.sent++;
          setProgress((prev) => ({ ...prev, sent: prev.sent + 1 }));
          onProgress(message.clientId, 'sent');
        } catch (error: any) {
          // Failure - update progress and continue
          result.failed++;
          const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';

          result.errors.push({
            clientId: message.clientId,
            clientName: message.clientName,
            error: errorMessage,
          });

          setProgress((prev) => ({ ...prev, failed: prev.failed + 1 }));
          onProgress(message.clientId, 'failed', errorMessage);

          console.error(`Failed to send message to ${message.clientName}:`, error);
        }

        // Wait 2 seconds before next message (except for last message)
        if (i < messages.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, RATE_LIMIT_MS));
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'messages'] });
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });

      setSending(false);
      return result;
    },
    [queryClient]
  );

  return {
    sendBulk,
    sending,
    progress,
  };
}
